import { tracker } from '@lessonup/analytics';
import {
  IconDislike,
  IconDislikeFill,
  IconLike,
  IconLikeFill,
  ModalFooter,
  ModalHeaderV1,
  SpaceBetween,
  spacing,
  styled,
  TextArea,
  Tooltip,
} from '@lessonup/ui-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPopover } from '../../../components/ButtonWithPopover/ButtonWithPopover';
import { AiToolsStandardGeneratorType, aiToolsTypeToEventType } from '../AiToolbox.types';
import { i18nextNamespaceAiTools } from '../AiToolbox.utils';

interface ThumbUpAndDownProps {
  aiToolType: AiToolsStandardGeneratorType;
}

export const ThumbUpAndDownFeedback = ({ aiToolType }: ThumbUpAndDownProps) => {
  const { t } = useTranslation(i18nextNamespaceAiTools, { keyPrefix: 'feedback' });
  const [thumbState, setThumbState] = useState<'up' | 'down' | null>(null);

  const aiToolsEventType = aiToolsTypeToEventType[aiToolType];

  const handleSetThumbsup = () => {
    if (thumbState === 'down') {
      sendCancelEvent('down');
    }
    setThumbState('up');
  };

  const handleSetThumbsdown = () => {
    if (thumbState === 'up') {
      sendCancelEvent('up');
    }
    setThumbState('down');
  };

  const sendCancelEvent = (type: 'up' | 'down') => {
    if (type === 'up') {
      tracker.events.aiToolsThumbsUpCancel({
        aiToolType: aiToolsEventType,
      });
    } else {
      tracker.events.aiToolsThumbsDownCancel({
        aiToolType: aiToolsEventType,
      });
    }
  };

  // We send the event after the modal is closed, either by clicking the button or outside the modal
  const handleModalOnClose = (type: 'up' | 'down', data?: OnCloseData) => {
    const commentInput = isCloseData(data) ? data.commentInput : undefined;
    if (type === 'up') {
      tracker.events.aiToolsThumbsUp({
        aiToolType: aiToolsEventType,
        commentInput: commentInput,
      });
    } else {
      tracker.events.aiToolsThumbsDown({
        aiToolType: aiToolsEventType,
        commentInput: commentInput,
      });
    }
  };

  return (
    <SpaceBetween direction="x" spacing={spacing.size8}>
      <Tooltip content={t('tooltipUp')} direction="top">
        <ButtonWithPopover
          buttonShape="rect"
          iconStart={thumbState === 'up' ? <IconLikeFill /> : <IconLike />}
          buttonType="neutral"
          showStroke
          modalConfig={{
            onBeforeOpen: () => {
              if (thumbState === 'up') {
                setThumbState(null);
                sendCancelEvent('up');
                return false;
              }
              return true;
            },
            onClose: (data) => handleModalOnClose('up', data),
            onOpen: handleSetThumbsup,
            contentLabel: t('modal.contentLabel'),
            modalBody: (close) => <ModalBody onClose={close as OnClose} />,
          }}
        ></ButtonWithPopover>
      </Tooltip>
      <Tooltip content={t('tooltipDown')} direction="top">
        <ButtonWithPopover
          buttonShape="rect"
          iconStart={thumbState === 'down' ? <IconDislikeFill /> : <IconDislike />}
          buttonType="neutral"
          showStroke
          modalConfig={{
            onBeforeOpen: () => {
              if (thumbState === 'down') {
                setThumbState(null);
                sendCancelEvent('down');
                return false;
              }
              return true;
            },
            onClose: (data) => handleModalOnClose('down', data),
            onOpen: handleSetThumbsdown,
            contentLabel: t('modal.contentLabel'),
            modalBody: (close) => <ModalBody onClose={close as OnClose} />,
          }}
        ></ButtonWithPopover>
      </Tooltip>
    </SpaceBetween>
  );
};

interface OnCloseData {
  commentInput?: string;
}
type OnClose = (data?: OnCloseData) => void;

function isCloseData(data: unknown): data is OnCloseData {
  return typeof data === 'object' && data !== null && 'commentInput' in data;
}

interface ModalBodyProps {
  onClose: OnClose;
}

const ModalBody = ({ onClose }: ModalBodyProps) => {
  const { t } = useTranslation(i18nextNamespaceAiTools, { keyPrefix: 'feedback' });
  const [commentInput, setCommentInput] = useState('');

  return (
    <>
      <ModalHeaderV1
        title={{ content: t('modal.headline'), size: 'M' }}
        type="headline"
        onCloseButtonClick={() => onClose()}
      />
      <ModalTextArea
        size="medium"
        placeholder={t('modal.placeholder')}
        rows={4}
        maxLength={1000}
        onChange={(e) => {
          setCommentInput(e.target.value);
        }}
      />
      <ModalFooter
        type="actions"
        primaryAction={{
          onClick: () => {
            onClose({
              commentInput,
            });
          },
          label: t('modal.button'),
        }}
      ></ModalFooter>
    </>
  );
};

const ModalTextArea = styled(TextArea)`
  padding: ${spacing.size12} ${spacing.size16} 0 ${spacing.size16};
  min-width: 300px;
`;
